import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  untracked,
  viewChildren,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { QuickSearchFacadeService } from '../services/quick-search-facade.service';
import { QUICK_SEARCH_TABS } from '@fc-search/models/quick-search';
import { QuickSearchTabsComponent } from './quick-search-tabs.component';
import { QuickSearchFooterComponent } from './quick-search-footer.component';
import { QuickSearchRecentComponent } from './quick-search-recent.component';
import { SkeletonModule } from '@fc-shared/ui/skeleton/skeleton.module';
import { QsVehicleResultComponent } from './quick-search-results/qs-vehicle-result.component';
import { QsEmployeeResultComponent } from './quick-search-results/qs-employee-result.component';
import { QsTaskResultComponent } from './quick-search-results/qs-task-result.component';
import { QsLocationResultComponent } from './quick-search-results/qs-location-result.component';
import { QsContactResultComponent } from './quick-search-results/qs-contact-result.component';
import { QsCompanyResultComponent } from './quick-search-results/qs-company-result.component';
import { QsChannelResultComponent } from './quick-search-results/qs-channel-result.component';
import { QsWorkflowResultComponent } from './quick-search-results/qs-workflow-result.component';
import { EmptyStateComponent } from '@fc-shared/ui/empty-state/empty-state.component';

const TYPE_TO_ENTITY: Record<string, string> = {
  vehicle_vehicle: 'vehicles',
  authentication_employee: 'employees',
  poi_location: 'locations',
  task: 'tasks',
  chat_channel: 'chats',
};

const TYPE_LABEL: Record<string, string> = {
  all: 'View All Results',
  vehicle_vehicle: 'View All Vehicle Results',
  authentication_employee: 'View All Employee Results',
  poi_location: 'View All Location Results',
  task: 'View All Task Results',
  chat_channel: 'View All Channel Results',
  crm: 'View All CRM Results',
};

@Component({
  selector: 'fc-quick-search-results',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    RouterLink,
    QuickSearchTabsComponent,
    QuickSearchFooterComponent,
    QuickSearchRecentComponent,
    SkeletonModule,
    QsVehicleResultComponent,
    QsEmployeeResultComponent,
    QsTaskResultComponent,
    QsLocationResultComponent,
    QsContactResultComponent,
    QsCompanyResultComponent,
    QsChannelResultComponent,
    QsWorkflowResultComponent,
    EmptyStateComponent,
  ],
  template: `
    <div
      class="flex flex-col h-full bg-white rounded-lg shadow-lg border border-secondary-100 overflow-hidden"
    >
      <!-- Type filter tabs -->
      <fc-quick-search-tabs
        [tabs]="tabs"
        [selectedType]="facade.selectedType()"
        (tabSelected)="facade.setType($event)"
      />

      <!-- Results body -->
      <div
        class="flex-1 min-h-0 overflow-y-auto md:flex-none md:overflow-visible"
      >
        @if (facade.loading()) {
          <fc-skeleton-list [count]="5" />
        } @else if (facade.query()) {
          @if (facade.results().length > 0) {
            <div
              class="md:max-h-[400px] md:overflow-y-auto"
              (click)="facade.clearAndClose()"
            >
              @for (
                item of facade.results();
                track item.entityType + item.obj.id;
                let i = $index
              ) {
                <div
                  #resultItem
                  class="relative animate-fade-in"
                  (click)="recordOriginal(i, $event)"
                >
                  @switch (item.entityType) {
                    @case ('vehicle_vehicle') {
                      <fc-qs-vehicle-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                    @case ('authentication_employee') {
                      <fc-qs-employee-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                    @case ('task_task') {
                      <fc-qs-task-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                    @case ('task_workflow') {
                      <fc-qs-workflow-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                    @case ('poi_location') {
                      <fc-qs-location-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                    @case ('crm_contact') {
                      <fc-qs-contact-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                    @case ('crm_company') {
                      <fc-qs-company-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                    @case ('chat_channel') {
                      <fc-qs-channel-result
                        #resultComp
                        [result]="item"
                        [selected]="facade.selectedIndex() === i"
                        [activeButtonIndex]="
                          facade.selectedIndex() === i
                            ? facade.selectedButtonIndex()
                            : -1
                        "
                        (quickAction)="recordQuickAction(i)"
                      />
                    }
                  }
                </div>
              }
              <!-- View All row -->
              <a
                #resultItem
                [routerLink]="['/search']"
                [queryParams]="viewAllParams()"
                (click)="facade.clearSearch()"
                class="flex items-center gap-12 px-16 py-10 hover:bg-secondary-50 cursor-pointer border-t border-secondary-100 no-underline"
                [class.border-l-2]="viewAllSelected()"
                [class.border-primary-600]="viewAllSelected()"
                [class.bg-primary-50]="viewAllSelected()"
              >
                <div
                  class="size-40 shrink-0 rounded-full bg-secondary-100 flex items-center justify-center"
                >
                  <fc-icon
                    icon="icon-search-outline"
                    [size]="20"
                    class="text-secondary-500"
                  />
                </div>
                <span class="text-sm text-secondary-700">
                  {{ viewAllLabel() }} For: {{ facade.query() }}
                </span>
              </a>
            </div>
          } @else {
            <fc-empty-state
              icon="icon-search-outline"
              title="No results"
              [subtitle]="emptyStateSubtitle()"
            />
          }
        } @else {
          @if (facade.recentItems().length > 0) {
            <fc-quick-search-recent [active]="active()" />
          } @else {
            <fc-empty-state
              icon="icon-search-outline"
              title="Start typing to search"
              subtitle="Quickly find what you're looking for"
            />
          }
        }
      </div>

      <!-- Footer -->
      <fc-quick-search-footer
        class="mt-auto"
        [viewAllParams]="viewAllParams()"
        (searchCleared)="facade.clearAndClose()"
      />
    </div>
  `,
})
export class QuickSearchResultsComponent {
  facade = inject(QuickSearchFacadeService);
  tabs = QUICK_SEARCH_TABS;
  active = input(true);

  private resultElements = viewChildren<ElementRef>('resultItem');
  private resultComps = viewChildren<{ activateButton(index: number): void }>(
    'resultComp',
  );

  emptyStateSubtitle = computed(() => {
    return (
      'Nothing found for ' +
      this.facade.query() +
      ' query.\nClick enter to try global search or adjust your query.'
    );
  });

  viewAllSelected = computed(
    () =>
      this.facade.results().length > 0 &&
      this.facade.selectedIndex() === this.facade.results().length,
  );

  viewAllLabel = computed(
    () => TYPE_LABEL[this.facade.selectedType()] ?? 'View All Results',
  );

  viewAllParams = computed(() => {
    const entity = TYPE_TO_ENTITY[this.facade.selectedType()];
    return entity
      ? { query: this.facade.query(), entity }
      : { query: this.facade.query() };
  });

  constructor() {
    afterRenderEffect(() => {
      const idx = this.facade.selectedIndex();
      if (idx >= 0) {
        this.resultElements()[idx]?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });

    let lastActivation = 0;
    effect(() => {
      const activation = this.facade.buttonActivated();
      if (activation > lastActivation) {
        lastActivation = activation;
        if (!untracked(() => this.active())) return;
        const idx = untracked(() => this.facade.selectedIndex());
        const btnIdx = untracked(() => this.facade.selectedButtonIndex());
        if (idx >= 0 && btnIdx >= 0) {
          const comp = untracked(() => this.resultComps()[idx]);
          comp?.activateButton?.(btnIdx);
        }
      }
    });
  }

  recordOriginal(index: number, event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button')) return;
    this.facade.trackItemOpened(this.facade.results()[index]);
  }

  recordQuickAction(index: number): void {
    this.facade.trackItemOpened(this.facade.results()[index]);
  }
}
